import { IFacetValue, INestedFacetValue, NestedCheckboxValues } from "JS/types/Search";

/**
 * Flattens an array of nested checkbox facet values into an object mapping
 * their `value`s to {@link INestedFacetValue}s.
 *
 * This is done to make lookup and manipulation of selections more efficient
 * and easier to reason about in an immutable way vs. the actual nested tree
 * structure which the backend (and Hawk) provides.
 *
 * Historically, this transformation was even more necessary, because Hawk
 * return _all_ values at the top level of an array, where hierarchy was determined
 * by a different path format encoded in their values.
 *
 * @param facetValues Initial facet values returned by the backend
 * @returns A lookup map of facetValue.value to INestedFacetValue (purely a frontend type)
 */
export function buildNestedCheckboxValues(facetValues: IFacetValue[]): NestedCheckboxValues {
	return recurse(facetValues, null);

	function recurse(facetValues: IFacetValue[], parent: IFacetValue | null): NestedCheckboxValues {
		const valueMap: { [id: string]: INestedFacetValue } = {};

		for (const facetValue of facetValues) {
			const mappedValue = {
				...facetValue,
				children: [],
				parent: parent?.value,
			};

			valueMap[facetValue.value] = mappedValue;

			if (facetValue?.children) {
				mappedValue.children.push(...facetValue.children.map(c => c.value));
				Object.assign(valueMap, recurse(facetValue.children, facetValue));
			}
		}

		return valueMap;
	}
}


/**
 * Returns an array of all of `facetValue`'s children, grandchildren, etc.
 *
 * @param map The nested checkbox lookup map
 * @param facetValue Facet value
 * @returns An array of all the children of `facetValue`
 */
export function getAllChildren(map: NestedCheckboxValues, facetValue: INestedFacetValue): INestedFacetValue[] {
	return Object.values(map).filter(nfv => nfv.value !== facetValue.value && nfv.path?.startsWith(facetValue.path));
}

/**
 * Determines whether any child at arbitrary depth in `facetValue`'s subtree is selected
 * @param map The nested checkbox lookup map
 * @param facetValue Facet value
 * @returns True if a child at arbitrary depth in `facetValue`'s subtree is selected, false otherwise
 */
export function isChildSelected(map: NestedCheckboxValues, facetValue: INestedFacetValue): boolean {
	return getAllChildren(map, facetValue).some(c => c.isSelected);
}

/**
 * Toggles a nested checkbox value and makes the necessary updates to its parent and/or children. Immutability is
 * significant here so that we can use this calculation without causing state updates (ex. generating links for
 * what the URL state _will be_ if a nested checkbox changes) and for easy testability.
 *
 * @param map The nested checkbox lookup map
 * @param value value property of the facet value to toggle
 * @returns The updated copy of `map`
 */
export function toggleNestedFacetValue(map: NestedCheckboxValues, value: string): NestedCheckboxValues {
	const updatedMap: typeof map = {
		...map,
	};

	const facetValue = updatedMap[value];

	const checked = !facetValue.isSelected;
	const parent = facetValue.parent ? updatedMap[facetValue.parent] : null;

	if (checked && parent) {
		updatedMap[parent.value] = {
			...parent,
			isSelected: false,
		};
	}

	for (const child of getAllChildren(updatedMap, facetValue)) {
		if (child.isSelected) {
			updatedMap[child.value] = {
				...child,
				isSelected: false,
			};
		}
	}

	updatedMap[facetValue.value] = {
		...facetValue,
		isSelected: !facetValue.isSelected,
	};

	if (!checked && parent && !isChildSelected(updatedMap, parent)) {
		updatedMap[parent.value] = {
			...parent,
			isSelected: true,
		};
	}

	return updatedMap;
}

/**
 * @param map The nested checkbox lookup map
 * @returns All selected facet values
 */
export function getSelectedNestedFacetValues(map: NestedCheckboxValues): INestedFacetValue[] {
	return Object.values(map).filter(fv => fv.isSelected);
}